import { type VariantProps, cva } from "class-variance-authority";
import type { LucideIcon } from "lucide-react";
import type { ComponentProps } from "react";

import { cn } from "@/lib/utils";

const iconButtonVariants = cva(
  "text-muted-foreground inline-grid shrink-0 cursor-pointer place-items-center rounded-chip " +
    "transition-colors disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      size: { md: "size-10", sm: "size-9" },
      tone: {
        default: "hover:bg-surface-muted hover:text-foreground",
        muted: "hover:bg-surface-muted",
        "danger-hover": "hover:bg-danger/12 hover:text-danger",
      },
      bordered: { true: "border-border-strong border", false: "" },
      active: { true: "bg-surface-muted text-foreground", false: "" },
    },
    defaultVariants: { size: "md", tone: "default", bordered: false, active: false },
  },
);

export interface IconButtonProps
  extends Omit<ComponentProps<"button">, "children">, VariantProps<typeof iconButtonVariants> {
  /** Nombre accesible. Obligatorio: un botón de solo ícono no tiene otro. */
  label: string;
  icon: LucideIcon;
}

export function IconButton({
  label,
  icon: Icon,
  size,
  tone,
  bordered,
  active,
  className,
  type = "button",
  ...props
}: IconButtonProps) {
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      className={cn(iconButtonVariants({ size, tone, bordered, active }), className)}
      {...props}
    >
      <Icon
        aria-hidden
        strokeWidth={1.75}
        className={size === "sm" ? "size-4" : "size-[18px]"}
      />
    </button>
  );
}
